import { Controller } from '@hotwired/stimulus';

/** Extensions traitées comme des IMAGES quand le navigateur ne descend AUCUN type
 *  MIME sur le `File` (cas connu : HEIC/HEIF sur certains Safari, et tout fichier
 *  dont l'OS ignore l'extension). Ce repli n'ÉLARGIT rien : `accept` seul décide
 *  de ce qui est accepté ; cette liste ne choisit que la FORME de la vignette.
 *  Type inconnu ⇒ tuile document, jamais un `<img src=blob:…>` cassé. */
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'heic', 'heif'];

/** Images qu'AUCUN navigateur hors Safari ne sait décoder. Elles restent des images
 *  pour le VOCABULAIRE (`_acceptsImagesOnly` ⇒ « photo ignorée », jamais « fichier »)
 *  et pour l'`accept` — le serveur les normalise en JPEG à l'ingestion — mais le
 *  fichier STAGÉ est encore du HEIC : un `blob:` posé sur un `<img>` y rend une icône
 *  cassée sous Chrome, Firefox et Edge, et le cadre resté câblé sur `image-preview`
 *  ouvre une visionneuse VIDE. Même doctrine que le PDF : tuile DOCUMENT. */
const UNDISPLAYABLE_IMAGE_EXTENSIONS = ['heic', 'heif'];

/** Le type MIME ne sauve rien : Chrome annonce « image/heic » sur macOS. Testé DEUX
 *  fois (extension puis MIME) parce qu'un fichier peut n'avoir ni l'une ni l'autre. */
const UNDISPLAYABLE_IMAGE_MIME = /^image\/hei[cf]/;

/** Glyphe de repli quand le fichier n'a pas d'extension. PAS de SVG inline neuf
 *  (piège #591) : la « vignette document » est faite de texte et de classes du
 *  design system, comme le placeholder du composant. */
const DOCUMENT_GLYPH_FALLBACK = 'DOC';

/** Refus d'un dépôt pendant un envoi (S12). Même canal que la notice de rejet. */
const BUSY_NOTICE = 'Envoi en cours. Déposez vos photos à nouveau dans un instant.';

/** Un nœud sous CE sélecteur, s'il n'est pas le dialog qui porte déjà la grille (le
 *  tiroir de remarque est lui-même un `role="dialog"`), est HORS zone (S13). */
const DIALOG_SELECTOR = '[role="dialog"]';
// `Node.TEXT_NODE`, en littéral pour rester lisible hors navigateur (tests node).
const TEXT_NODE = 3;

/**
 * photo-picker — "+" tile / file picker for the CR remark (remarque) photo grid and
 * for the agent console composer strip.
 *
 * Fixes the CR photo grid's dead "+" tile into a real, accessible file picker that
 * reuses the SAME thumbnail grid in BOTH modes (see `tasks/cr-522-photo-on-create-plan.md`
 * §4.4). It is mounted on the grid root, space-merged with the `image-preview`
 * controller. Two mutually-exclusive modes, driven by the `mode` value:
 *
 *   - "stage"  (CREATE — no Observation yet): accepted picks accumulate client-side
 *     into `this.files`; the hidden input's FileList is rewritten via `DataTransfer`
 *     so the multipart CREATE submit carries exactly the staged set; each new file
 *     renders a tile (cloned from `stagedTemplate`) inserted before the "+" tile,
 *     with a client-side ✕ (`remove`). No server round-trip. TWO tile flavours,
 *     decided per file by `_isImage` (see `_renderTile`):
 *       · IMAGE    → object-URL thumbnail + lightbox affordance wired to the shared
 *         `image-preview` controller on the same root (historical behaviour).
 *       · DOCUMENT → NO object URL, NO `<img>`, NO lightbox: the cloned tile's
 *         thumbnail frame is stripped of its preview wiring and re-filled with an
 *         extension glyph + the truncated file name (`_fillDocumentTile`).
 *         A `blob:` URL on an `<img>` would render a broken icon, and an
 *         `image-preview` lightbox would open an empty modal on a PDF — and just the
 *         same on a HEIC/HEIF, an "image" no browser but Safari decodes, which is why
 *         those two take the DOCUMENT flavour too (UNDISPLAYABLE_IMAGE_EXTENSIONS)
 *         while staying "photo" in the microcopy.
 *   - "submit" (EDIT — Observation persisted): the accepted picks are POSTed
 *     immediately by `requestSubmit()`-ing the sibling carrier form the hidden input
 *     is HTML5 `form=`-associated to; the server re-renders ONLY the grid container
 *     (Turbo Stream), so the in-progress edit form is preserved. The controller
 *     renders NO client tiles in submit mode — the server is the source of truth.
 *
 * The allow-list is read straight off the input's `accept` attribute and NOTHING is
 * hard-coded here — the SAME grid serves the CR remark photos
 * (`.jpg,.jpeg,.png,.heic,.heif`) and the agent console composer
 * (`.jpg,.jpeg,.png,.pdf`), so a frozen list would advertise the wrong contract on
 * one of the two. Picks are validated for size (≤ `maxSize` MB) and type before they
 * stage/submit. The French skip notice derives its format enumeration from that same
 * attribute (`_formatsLabel`), so it reads "JPG, PNG ou HEIC" on a CR remark grid and
 * "JPG, PNG ou PDF" on the console. Skips replace the neutral
 * format hint with a red French notice; a subsequent clean pick or a remove restores
 * the neutral hint (its default text comes from the hint element's
 * `data-photo-picker-default-hint-value`, not its live textContent — a server
 * re-render may leave an error string there). Object URLs are created for IMAGE
 * tiles only, tracked and revoked on `remove` / `clear` / `disconnect` (they stay
 * valid while the tile exists, so the lightbox works).
 *
 * Targets:
 *   - input:          hidden <input type=file multiple accept=".jpg,.jpeg,.png[,.heic,.heif][,.pdf]">
 *                     ⚠️ ILLUSTRATION, jamais un gabarit à recopier : l'`accept` RÉEL
 *                     vient du serveur, et il DIFFÈRE par appelant (la grille de
 *                     remarque accepte HEIC, la console non — régime Blob non
 *                     normalisé). Copier l'exemple livrerait le mauvais `accept`.
 *   - grid:           the <ul> the tiles live in
 *   - addTile:        the "+" tile, OPTIONNELLE (staged tiles are inserted before
 *                     it, sinon en fin de bande — l'appelant porte alors sa
 *                     propre affordance d'ajout)
 *   - stagedTemplate: <template> cloned per staged file (thumbnail <img> + ✕ +
 *                     lightbox). UN SEUL gabarit, partagé par les 5 appelants de
 *                     `Ui::ThumbnailGridComponent` : la variante document est
 *                     obtenue en MUTANT le clone, jamais en forkant le gabarit.
 *   - hint:           <p aria-live=polite> format hint doubling as the skip-notice region
 *
 * REPLI OPT-IN (bande de compositeur, console dev agent). Quand le serveur descend
 * `collapsedClass`, le contrôleur bascule cette classe — et AUCUNE autre, il ne
 * connaît aucun token Tailwind (D14) — sur DEUX nœuds :
 *   - sa RACINE : masquée tant qu'il n'y a NI vignette NI message de rejet, de
 *     sorte que le compositeur au repos est UNE rangée, comme le mockup ;
 *   - le `<p>` hint : masqué tant qu'il ne porte pas de texte, pour qu'un rejet
 *     format/taille reste lisible MÊME sans la moindre miniature.
 * Sans cette value (les deux grilles de remarque CR), `_syncCollapse()` sort au
 * premier `if` : ces grilles restent visibles en permanence, comportement inchangé.
 *
 * GLISSER-DÉPOSER OPT-IN (grilles de remarque CR seulement, `droppable:` côté
 * composant). La racine est la zone : un fichier déposé dessus suit EXACTEMENT le
 * chemin du « + » (`_accept`), sauf le focus, qu'un dépôt ne déplace jamais (S14).
 *   - seul un glisser qui porte `Files` compte (texte et liens gardent le natif) ;
 *   - un compteur de profondeur enter/leave empêche le ring de scintiller au-dessus
 *     des vignettes ; `connect()` le remet à zéro (re-rendu Turbo en édition) ;
 *   - le garde `@window` annule le `dragover` de fichiers HORS zone
 *     (`dropEffect = 'none'`, donc aucun `drop` ne suit) : un quasi-raté n'ouvre
 *     jamais le fichier à la place du formulaire. C'est aussi LUI qui éteint le
 *     ring quand le pointeur part ailleurs, avec `windowLeave` hors de la fenêtre ;
 *   - un nœud sous un `[role="dialog"]` imbriqué (la visionneuse) est hors zone ;
 *   - formulaire hôte, porteur ou `turbo-frame` en `aria-busy` ⇒ dépôt refusé avec
 *     la notice S12 (un second submit Turbo annulerait en silence celui en vol).
 * Les couleurs arrivent par l'API classes (`dragActiveClasses`, getter PLURIEL :
 * le singulier rendrait la chaîne multi-tokens en UN seul token, refusé par classList).
 *
 * Values:
 *   - mode:    String — "stage" (create) | "submit" (edit). Default "stage".
 *   - maxSize: Number — max file size in MB (0/absent = no size limit).
 *   - collapsedClass: String — token de repli (défaut "" = repli DÉSACTIVÉ). La
 *     value est à la fois l'opt-in et le token : rien n'est écrit en dur ici.
 *
 * Classes:
 *   - dragActive: tokens posés sur la racine pendant un glisser de fichiers
 *     (absente ⇒ aucun ring, le dépôt fonctionne quand même).
 *
 * Actions:
 *   - click->photo-picker#browse  (the "+" button)
 *   - change->photo-picker#change (the hidden input)
 *   - click->photo-picker#remove  (each staged tile's ✕)
 *   - sur la racine déposable : dragenter / dragover / dragleave / drop, plus
 *     dragover@window + drop@window -> #guard et dragleave@window -> #windowLeave
 *     (chaîne exacte : `Ui::ThumbnailGridComponent::DROP_ACTIONS`)
 *
 * Outlet API (methods a HOST controller may call): `browse()` and `clear()`.
 * `clear()` drops the whole staged set — needed by hosts whose submit response
 * re-renders nothing (see the method's own comment).
 *
 * @example
 *   <ul data-controller="image-preview photo-picker"
 *       data-photo-picker-mode-value="stage"
 *       data-photo-picker-max-size-value="10">
 *     <li> … existing thumbnail … </li>
 *     <li data-photo-picker-target="addTile">
 *       <button type="button" data-action="click->photo-picker#browse"
 *               aria-label="Ajouter une photo" aria-describedby="remark-photos-alert-OBS-3">+</button>
 *     </li>
 *     <input type="file" multiple accept=".jpg,.jpeg,.png[,.heic,.heif]" class="sr-only"
 *            name="observation[photos][]"
 *            data-photo-picker-target="input"
 *            data-action="change->photo-picker#change">
 *     <template data-photo-picker-target="stagedTemplate">
 *       <li>
 *         <div role="button" tabindex="0"
 *              data-action="click->image-preview#open keydown->image-preview#openKeyboard"
 *              data-image-preview-url-param="" data-image-preview-filename-param="">
 *           <img data-photo-picker-target="stagedImage" alt="">
 *         </div>
 *         <button data-action="click->photo-picker#remove">✕</button>
 *       </li>
 *     </template>
 *     <p data-photo-picker-target="hint" data-photo-picker-default-hint-value="<formats> · <N> Mo max"
 *        aria-live="polite"><formats> · <N> Mo max</p>
 *   </ul>
 */
export default class extends Controller {
  static targets = ['input', 'grid', 'addTile', 'stagedTemplate', 'hint'];
  static classes = ['dragActive'];
  static values = {
    mode: { type: String, default: 'stage' },
    maxSize: { type: Number, default: 0 },
    collapsedClass: { type: String, default: '' },
  };

  connect() {
    // Staged set (stage mode only), source of truth for the DataTransfer FileList.
    // Stable monotonic ids survive removals; an IMAGE entry keeps its object URL so
    // it can be revoked on remove/disconnect (a document entry carries `url: null`).
    this.files = [];
    this.nextId = 0;
    // Profondeur du glisser (enter/leave) : repart de zéro à chaque (re)connexion,
    // ring éteint, pour qu'un re-rendu Turbo pendant un glisser ne le coince pas.
    this._endDrag();
    // Turbo restore / re-render : le serveur a déjà rendu l'état correct, mais un
    // cache de retour arrière pourrait ramener une bande dévoilée sans vignette.
    this._syncCollapse();
  }

  disconnect() {
    // Revoke every outstanding object URL — Turbo destroys/reconnects controllers
    // on navigation and grid re-renders, so leaked blob URLs would accumulate.
    this.files.forEach((entry) => this._revoke(entry));
    this.files = [];
  }

  // Open the native file picker.
  browse() {
    this.inputTarget.click();
  }

  // A « + » pick: same pipeline as a drop, and focus returns to the « + » button.
  change(event) {
    this._accept(Array.from(event.target.files || []), { refocus: true });
  }

  // --- glisser-déposer (racine déposable) ---------------------------------

  dragenter(event) {
    if (!this._isZoneFileDrag(event)) return;

    event.preventDefault();
    this.dragDepth += 1;
    this._setDragActive(true);
  }

  dragover(event) {
    if (!this._isZoneFileDrag(event)) return;

    // Sans preventDefault le navigateur refuse le dépôt et ouvre le fichier.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  // Passer de la racine à une vignette émet enter(enfant) PUIS leave(racine) : seul
  // le leave qui ramène la profondeur à zéro sort vraiment de la zone.
  dragleave(event) {
    if (!this._isZoneFileDrag(event)) return;

    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) this._setDragActive(false);
  }

  drop(event) {
    if (!this._isZoneFileDrag(event)) return;

    event.preventDefault();
    this._endDrag();

    // S9'/S12 : refusé, jamais mis en file (un second submit annulerait celui en vol).
    if (this._isBusy()) {
      this._showBusy();
      return;
    }

    const files = Array.from(event.dataTransfer.files || []);
    if (files.length === 0) return;

    // S14 : un dépôt ne déplace jamais le focus (l'utilisateur tape peut-être ailleurs).
    this._accept(files, { refocus: false });
  }

  // Garde @window (S5' : actif tant que la grille est connectée, tiroir fermé
  // compris). Annule un glisser de fichiers HORS zone : le `dragover` annulé avec
  // `dropEffect = 'none'` empêche le navigateur d'ouvrir le fichier, et comme aucun
  // `drop` ne suit alors, c'est ICI que le ring s'éteint quand le pointeur part.
  // Un événement déjà annulé (par une zone, celle-ci ou une autre) n'est pas retouché.
  guard(event) {
    if (event.defaultPrevented || !this._isFileDrag(event)) return;
    if (this._inZone(event.target)) return;

    event.preventDefault();
    if (event.type === 'dragover') event.dataTransfer.dropEffect = 'none';
    this._endDrag();
  }

  // Le pointeur quitte la FENÊTRE : `relatedTarget` nul ET pointeur au bord ou
  // dehors. Chrome émet aussi un `relatedTarget` nul DANS la page : on l'ignore.
  windowLeave(event) {
    if (!this._isFileDrag(event) || event.relatedTarget) return;

    const { clientX: x, clientY: y } = event;
    const outside = x <= 0 || y <= 0 || x >= window.innerWidth || y >= window.innerHeight;
    if (outside) this._endDrag();
  }

  // Drop one staged file (by its stable id), re-sync the FileList, remove the tile,
  // and revoke its object URL. Restores the neutral hint and returns focus to the
  // "+" button (the ✕ is gone).
  remove(event) {
    const tile = event.currentTarget.closest('[data-file-id]');
    if (!tile) return;

    const id = Number(tile.dataset.fileId);
    const entry = this.files.find((e) => e.id === id);
    if (entry) this._revoke(entry);

    this.files = this.files.filter((e) => e.id !== id);
    this._syncInput();
    tile.remove();
    this._restoreHint();
    this._focusAddButton();
  }

  // Drop the WHOLE staged set: revoke every object URL, remove every staged tile,
  // empty the input's FileList and restore the neutral hint. Additive and called
  // by NO existing CR call site — it exists for hosts whose submit answers with a
  // status that re-renders nothing (the agent console POSTs and gets `no_content`,
  // so without this the same photos would ride the NEXT message too). A no-op in
  // "submit" mode, where `this.files` is never populated.
  clear() {
    this.files.forEach((entry) => {
      this._revoke(entry);
      this.element.querySelector(`[data-file-id="${entry.id}"]`)?.remove();
    });
    this.files = [];
    this._resetInput();
    this._restoreHint();
  }

  // --- internals ---------------------------------------------------------

  // Validate a pick or a drop (size + `accept`), then stage or submit it. `refocus`
  // is true for a « + » pick only (S14).
  _accept(files, { refocus }) {
    // Clear any leftover red notice first (skip OR busy); a clean pick leaves the
    // neutral hint.
    this._restoreHint();

    let skipped = 0;
    const accepted = [];

    files.forEach((file) => {
      // Dedupe only matters while accumulating a staged set (submit picks are
      // independent, server-deduped).
      if (this.modeValue === 'stage' && this._isDuplicate(file)) return;

      if (this._rejectionReason(file)) {
        skipped += 1;
        return;
      }
      accepted.push(file);
    });

    if (skipped > 0) this._showSkips(skipped);

    if (this.modeValue === 'submit') {
      this._submitPicked(accepted, refocus);
    } else {
      this._stagePicked(accepted, refocus);
    }
  }

  // EDIT: rewrite the input to the accepted subset and requestSubmit() the carrier
  // form (the input is HTML5 `form=`-associated to it). requestSubmit() snapshots the
  // FileList synchronously, so we reset the input right after — the same file can be
  // re-picked and the server re-renders the grid via Turbo Stream.
  _submitPicked(accepted, refocus) {
    if (accepted.length === 0) {
      this._resetInput();
      if (refocus) this._focusAddButton();
      return;
    }

    const dataTransfer = new DataTransfer();
    accepted.forEach((file) => dataTransfer.items.add(file));
    this.inputTarget.files = dataTransfer.files;

    this._carrierForm()?.requestSubmit();

    this._resetInput();
    if (refocus) this._focusAddButton();
  }

  // CREATE: accumulate into `this.files`, rewrite the input FileList so the multipart
  // create submit carries the staged set, and render one tile per file.
  _stagePicked(accepted, refocus) {
    accepted.forEach((file) => {
      // AUCUN object URL pour un document : rien ne l'afficherait, et une URL
      // `blob:` non révoquée retiendrait les octets du PDF jusqu'au déchargement
      // de la page. `_revoke` tolère déjà `url: null`.
      const isImage = this._isImage(file);
      const entry = {
        id: this.nextId,
        file,
        isImage,
        url: isImage ? URL.createObjectURL(file) : null,
      };
      this.nextId += 1;
      this.files.push(entry);
      this._renderTile(entry);
    });

    this._syncInput();
    this._syncCollapse();
    if (refocus) this._focusAddButton();
  }

  // Clone the staged-tile template, tag the tile with the stable id (so `remove` can
  // find it), fill it as an IMAGE or as a DOCUMENT, and insert it before the "+" tile.
  //
  // Le gabarit `stagedTemplate` vit dans `Ui::ThumbnailGridComponent`, PARTAGÉ par ses
  // 5 appelants (compositeur de la console, les 2 grilles de remarque CR, les 2 previews
  // Lookbook) : la variante document se fabrique donc en MUTANT le clone ici, et pas un
  // octet du composant ne bouge.
  _renderTile(entry) {
    const fragment = this.stagedTemplateTarget.content.cloneNode(true);
    const tile = fragment.querySelector('li') ?? fragment.firstElementChild;
    if (!tile) return;

    tile.dataset.fileId = entry.id;

    if (entry.isImage) this._fillImageTile(tile, entry);
    else this._fillDocumentTile(tile, entry.file);

    // Sans tuile « + » (l'appelant porte son affordance ailleurs) il n'y a pas de
    // nœud d'ancrage : la vignette part en fin de bande.
    if (this.hasAddTileTarget) this.addTileTarget.before(tile);
    else this.gridTarget.append(tile);
  }

  // IMAGE — comportement historique : la miniature pointe l'object URL et le cadre
  // ouvre la lightbox partagée (le contrôleur `image-preview` est sur la même racine)
  // avec cette URL, valide tant que la tuile existe.
  _fillImageTile(tile, entry) {
    const thumbnail = this._tileImage(tile);
    if (thumbnail) thumbnail.src = entry.url;

    const previewTrigger = tile.querySelector('[data-image-preview-url-param]');
    if (previewTrigger) {
      previewTrigger.dataset.imagePreviewUrlParam = entry.url;
      previewTrigger.dataset.imagePreviewFilenameParam = entry.file.name;
    }
  }

  // DOCUMENT — ni `<img>`, ni object URL, ni lightbox : un `blob:` de PDF posé sur un
  // `<img>` rend une icône cassée, et `image-preview` ouvrirait une modale vide. Le
  // cadre de miniature est DÉCÂBLÉ (rôle, tabindex, `data-action`, params de preview)
  // AVANT insertion dans le document — Stimulus ne lie ses actions qu'à l'insertion,
  // donc rien n'est jamais branché — puis re-rempli d'un glyphe d'extension et du nom
  // du fichier. La croix (`photo-picker#remove`) et le `data-file-id` sont intacts :
  // retrait et compteur du lot se comportent EXACTEMENT comme pour une photo.
  _fillDocumentTile(tile, file) {
    const image = this._tileImage(tile);
    const frame = tile.querySelector('[data-image-preview-url-param]') ?? image?.parentElement;
    if (!frame) return;

    image?.remove();

    [
      'role',
      'tabindex',
      'aria-label',
      'data-action',
      'data-image-preview-url-param',
      'data-image-preview-filename-param',
    ].forEach((attribute) => frame.removeAttribute(attribute));
    // Plus rien n'est cliquable ici : garder le curseur main annoncerait une
    // affordance qui n'existe plus.
    frame.classList.remove('cursor-pointer');
    frame.classList.add(
      'flex',
      'flex-col',
      'items-center',
      'justify-center',
      'px-1',
      'text-center'
    );
    // Le nom COMPLET reste lisible au survol. `setAttribute` sur un ATTRIBUT : la
    // donnée utilisateur n'entre jamais dans du HTML (piège #591).
    frame.setAttribute('title', file.name);

    frame.append(this._documentGlyph(file), this._documentLabel(file));
  }

  // Le glyphe : l'extension en capitales, `aria-hidden` (le nom juste en dessous la
  // porte déjà). AUCUN SVG inline neuf (piège #591) — du texte et des classes du
  // design system, comme le placeholder du composant.
  _documentGlyph(file) {
    const glyph = document.createElement('span');
    glyph.className = 'text-sm font-semibold uppercase text-neutral-500';
    glyph.setAttribute('aria-hidden', 'true');
    glyph.textContent =
      this._extensionOf(file.name).slice(0, 4).toUpperCase() || DOCUMENT_GLYPH_FALLBACK;
    return glyph;
  }

  // Le nom de fichier est de la DONNÉE UTILISATEUR : `textContent`, JAMAIS
  // `innerHTML`. La troncature est portée par `truncate` (ellipsis CSS) sur une
  // tuile de largeur fixe, l'extension étant déjà rendue par le glyphe.
  _documentLabel(file) {
    const label = document.createElement('span');
    label.className = 'w-full truncate text-xs leading-tight text-neutral-500';
    label.textContent = this._baseNameOf(file.name);
    return label;
  }

  _tileImage(tile) {
    return (
      tile.querySelector('[data-photo-picker-target="stagedImage"]') ?? tile.querySelector('img')
    );
  }

  // Rebuild the input's FileList from `this.files` so the form submits exactly the
  // staged set. (Assigning `.files` programmatically does NOT re-fire `change`.)
  _syncInput() {
    const dataTransfer = new DataTransfer();
    this.files.forEach((entry) => dataTransfer.items.add(entry.file));
    this.inputTarget.files = dataTransfer.files;
  }

  // Replace the neutral format hint with a red French skip notice (proper feminine
  // agreement — no "(s)" literals).
  //
  // Le SUJET et les FORMATS sont dérivés de l'attribut `accept`, jamais figés ici :
  // une grille image-seule rend « 1 photo ignorée : JPG, PNG ou HEIC, 25 Mo max. »
  // (les 2 grilles de remarque du CR), une grille qui accepte aussi le PDF rend
  // « 1 fichier ignoré : JPG, PNG ou PDF, 10 Mo max. ». Une liste en dur y
  // annoncerait le mauvais contrat (anti-patron `subcontract_cap_controller.js` ;
  // précédent `Form::DropzoneComponent#formats_label`).
  _showSkips(count) {
    if (!this.hasHintTarget) return;

    const imageOnly = this._acceptsImagesOnly();
    let subject;
    if (count === 1) subject = imageOnly ? '1 photo ignorée' : '1 fichier ignoré';
    else subject = imageOnly ? `${count} photos ignorées` : `${count} fichiers ignorés`;

    const constraints = [
      this._formatsLabel(),
      this.maxSizeValue > 0 ? `${this.maxSizeValue} Mo max` : null,
    ]
      .filter(Boolean)
      .join(', ');

    this._showNotice(constraints ? `${subject} : ${constraints}.` : `${subject}.`);
  }

  // S12 : dépôt refusé pendant un envoi. Même canal que les skips ; le prochain
  // choix ou dépôt accepté rend le hint neutre (`_accept` → `_restoreHint`).
  _showBusy() {
    if (!this.hasHintTarget) return;

    this._showNotice(BUSY_NOTICE);
  }

  // Le hint devient une notice rouge. Seul site qui pose le style danger, pendant
  // exact de `_restoreHint`.
  _showNotice(text) {
    this.hintTarget.textContent = text;
    this.hintTarget.classList.remove('text-neutral-500');
    this.hintTarget.classList.add('text-danger-600');
    this._syncCollapse();
  }

  // Restore the neutral format hint from its data-default (NOT its live textContent —
  // a server re-render may have left an error string there).
  _restoreHint() {
    if (!this.hasHintTarget) return;

    this.hintTarget.textContent = this.hintTarget.dataset.photoPickerDefaultHintValue || '';
    this.hintTarget.classList.remove('text-danger-600');
    this.hintTarget.classList.add('text-neutral-500');
    this._syncCollapse();
  }

  // Repli opt-in. `collapsedClassValue` est À LA FOIS l'interrupteur et le token :
  // absent (grilles CR), on sort ici et RIEN n'est jamais masqué. La racine ne
  // porte aucun utilitaire d'affichage (D3), donc la classe fait effet.
  _syncCollapse() {
    const token = this.collapsedClassValue;
    if (!token) return;

    const hasMessage = this._hintHasMessage();
    this.element.classList.toggle(token, !this._hasTiles() && !hasMessage);
    // Le hint est un CANAL : masqué à vide, il ne creuse pas de blanc sous une
    // bande dévoilée pour une seule vignette — et il reste lisible sans vignette.
    if (this.hasHintTarget) this.hintTarget.classList.toggle(token, !hasMessage);
  }

  // Vignettes réellement présentes : la tuile « + » est un `<li>` comme les autres
  // et la compter empêcherait toute bande de se replier.
  _hasTiles() {
    if (!this.hasGridTarget) return false;

    const addTile = this.hasAddTileTarget ? this.addTileTarget : null;
    return Array.from(this.gridTarget.children).some((child) => child !== addTile);
  }

  _hintHasMessage() {
    return this.hasHintTarget && this.hintTarget.textContent.trim() !== '';
  }

  _isDuplicate(file) {
    return this.files.some(
      (entry) =>
        entry.file.name === file.name &&
        entry.file.size === file.size &&
        entry.file.lastModified === file.lastModified
    );
  }

  // Truthy rejection reason, or null when the file matches `accept` and fits the size
  // bound. Size FIRST: an oversize PDF and an unsupported .docx are both "skipped",
  // and the notice names both bounds anyway.
  _rejectionReason(file) {
    if (this.maxSizeValue > 0 && file.size > this.maxSizeValue * 1024 * 1024) return 'oversize';
    if (!this._isAccepted(file)) return 'type';
    return null;
  }

  // Match the file against the input's `accept` list — SEULE source de vérité du
  // filtre client (rien n'est figé dans ce fichier) : `.jpg,.jpeg,.png,.heic,.heif`
  // sur les grilles CR, `.jpg,.jpeg,.png,.pdf` sur le compositeur. Le serveur
  // reste l'arbitre : ce filtre n'est qu'un pré-filtre.
  _isAccepted(file) {
    const tokens = this._acceptTokens();
    if (tokens.length === 0) return true;

    const name = file.name.toLowerCase();
    return tokens.some((token) => {
      if (token.startsWith('.')) return name.endsWith(token);
      if (token.endsWith('/*')) return file.type.startsWith(token.slice(0, -1));
      return file.type === token;
    });
  }

  _acceptTokens() {
    const accept = (this.inputTarget.getAttribute('accept') || '').trim();
    if (!accept) return [];

    return accept
      .split(',')
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean);
  }

  // Les extensions de l'`accept` (sans le point), dans l'ordre déclaré. `jpeg` est
  // écarté quand `jpg` est là : c'est le MÊME format, et le serveur écrit lui aussi
  // « JPG, PNG, PDF » (`show.html.erb`, title du trombone). Même dédoublonnage pour
  // `heif` sous `heic` : parité de microcopy avec le serveur, qui rend « JPG, PNG ou
  // HEIC » pour l'`accept` des photos de remarque (`RemarkFormComponent::PHOTO_ACCEPT`).
  _acceptedExtensions() {
    const extensions = this._acceptTokens()
      .filter((token) => token.startsWith('.'))
      .map((token) => token.slice(1));

    return extensions.filter(
      (ext) =>
        !(ext === 'jpeg' && extensions.includes('jpg')) &&
        !(ext === 'heif' && extensions.includes('heic'))
    );
  }

  // « JPG, PNG ou HEIC » / « JPG, PNG ou PDF ». Vide quand l'`accept` ne liste aucune
  // extension (MIME nus) : l'appelant retombe alors sur la seule borne de taille.
  _formatsLabel() {
    const labels = this._acceptedExtensions().map((ext) => ext.toUpperCase());
    if (labels.length <= 1) return labels.join('');

    return `${labels.slice(0, -1).join(', ')} ou ${labels[labels.length - 1]}`;
  }

  // Grille image-seule ⇒ le vocabulaire reste « photo » (CR). Dès qu'un format non
  // image est accepté, c'est « fichier ». Un `accept` vide ou sans extension accepte
  // tout : « fichier » est alors le mot juste.
  _acceptsImagesOnly() {
    const extensions = this._acceptedExtensions();
    return extensions.length > 0 && extensions.every((ext) => IMAGE_EXTENSIONS.includes(ext));
  }

  // Forme de la vignette (jamais l'acceptation, qui appartient à `accept` seul). Un
  // format que le navigateur ne DÉCODE pas sort d'abord, quoi qu'annonce son type MIME
  // (cf. UNDISPLAYABLE_IMAGE_EXTENSIONS) ; sinon le type MIME prime ; absent, on
  // retombe sur l'extension ; inconnue, on rend une tuile document plutôt qu'un `<img>`
  // potentiellement cassé.
  _isImage(file) {
    if (this._isUndisplayable(file)) return false;
    if (file.type) return file.type.startsWith('image/');

    return IMAGE_EXTENSIONS.includes(this._extensionOf(file.name));
  }

  // HEIC/HEIF, par l'extension OU par le type MIME : l'un des deux peut manquer.
  _isUndisplayable(file) {
    return (
      UNDISPLAYABLE_IMAGE_EXTENSIONS.includes(this._extensionOf(file.name)) ||
      UNDISPLAYABLE_IMAGE_MIME.test(file.type || '')
    );
  }

  _extensionOf(name) {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
  }

  _baseNameOf(name) {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(0, dot) : name;
  }

  // The carrier form the hidden input is `form=`-associated to (edit/submit mode).
  // `input.form` already resolves the `form=` attribute per the HTML spec; fall back
  // to an explicit id lookup for robustness.
  _carrierForm() {
    const id = this.inputTarget.getAttribute('form');
    return this.inputTarget.form ?? (id ? document.getElementById(id) : null);
  }

  _resetInput() {
    this.inputTarget.value = '';
  }

  // Un glisser de fichiers depuis l'OS (jamais du texte, un lien, une URL).
  _isFileDrag(event) {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  }

  // Garde commune des quatre handlers de la racine : un glisser de fichiers DANS la zone.
  _isZoneFileDrag(event) {
    return this._isFileDrag(event) && this._inZone(event.target);
  }

  // Dans la racine ET sous le même dialog qu'elle. On compare au dialog qui ENGLOBE
  // la racine, jamais à « aucun dialog » : le tiroir de remarque est lui-même un
  // `role="dialog"` (`Ui::SlideOverComponent` modal), et un test nu rendrait toute
  // la grille hors zone. La visionneuse imbriquée (S13), elle, diffère.
  // Firefox peut cibler un nœud TEXTE (le hint) : on remonte à son élément, qui
  // seul porte `closest`.
  _inZone(target) {
    const node = target?.nodeType === TEXT_NODE ? target.parentElement : target;
    if (!node || !this.element.contains(node)) return false;

    const targetDialog = node.closest?.(DIALOG_SELECTOR) ?? null;
    const rootDialog = this.element.closest?.(DIALOG_SELECTOR) ?? null;
    return targetDialog === rootDialog;
  }

  // Turbo pose `aria-busy="true"` sur le formulaire qui soumet et sur le cadre qui
  // charge. Hôte (la remarque), porteur (`form=` de l'input) et cadre du tiroir.
  _isBusy() {
    return [
      this.element.closest('form'),
      this._carrierForm(),
      this.element.closest('turbo-frame'),
    ].some((node) => node?.getAttribute('aria-busy') === 'true');
  }

  // Tokens via le getter PLURIEL (D14 : les couleurs du glisser viennent du serveur,
  // jamais d'un littéral ici).
  _setDragActive(on) {
    if (!this.hasDragActiveClass) return;

    if (on) this.element.classList.add(...this.dragActiveClasses);
    else this.element.classList.remove(...this.dragActiveClasses);
  }

  _endDrag() {
    this.dragDepth = 0;
    this._setDragActive(false);
  }

  _focusAddButton() {
    this._addButton()?.focus();
  }

  // The "+" button — the addTile target is the <li>; the button lives inside it (but
  // tolerate the target itself being the button).
  _addButton() {
    if (!this.hasAddTileTarget) return null;
    const el = this.addTileTarget;
    return el.matches('button') ? el : el.querySelector('button');
  }

  _revoke(entry) {
    if (entry.url) {
      URL.revokeObjectURL(entry.url);
      entry.url = null;
    }
  }
}
